import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios';
import type { ZodType } from 'zod';
import { apiBaseUrl } from '@/config/env';
import { ApiError } from './errors';
import { reportContractDrift } from './contract-drift';
import { getAuthRuntime } from './runtime';
import { STEP_UP_HEADER, STEP_UP_INVALID, STEP_UP_REQUIRED, getStepUpPrompter } from './step-up';

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  auth?: boolean;
  idempotencyKey?: string;
  signal?: AbortSignal;
  schema?: ZodType;
  /**
   * This request carries a secret the server consumes on sight — a TOTP code, a
   * one-shot challenge token. Two consequences, both mandatory:
   *
   *   - It is NEVER replayed. A repeat sends a secret the server already spent,
   *     so the second answer can only be another rejection, and on a TOTP route
   *     it costs a second attempt out of a rate limit measured in single digits.
   *   - Its 401 NEVER clears the session. The server is saying the secret is
   *     wrong, not that the caller is unauthenticated.
   *
   * The caller has to declare it because the interceptor cannot infer it: a 401
   * body looks the same either way, and on this backend the code-rejection 401
   * carries no `problem.code` to branch on.
   */
  oneTimeSecret?: boolean;
}

// Campos internos que viajan en la config de axios entre interceptores.
interface InternalConfig extends AxiosRequestConfig {
  _auth?: boolean;
  _retry?: boolean;
  _oneTimeSecret?: boolean;
}

// Cuerpo de error del backend (RFC 7807 Problem+JSON, ProblemExceptionFilter):
// { type, title, status, detail, instance, errors?, code?, timestamp }.
// El texto humano viaja en `detail` (nunca `message`); `errors` lleva el detalle
// de validación (o payloads como `duplicates` en el 409 de preguntas).
interface ProblemBody {
  code?: string;
  title?: string;
  detail?: string;
  errors?: unknown;
  // Tolerancia a formatos legacy/no-Problem que aún usen `message`.
  message?: string | string[];
}

function toApiError(error: AxiosError): ApiError {
  const res = error.response;
  if (res) {
    const data = (res.data ?? undefined) as ProblemBody | undefined;
    let code = 'HTTP_ERROR';
    let message = '';
    if (data && typeof data === 'object') {
      if (data.code) code = data.code;
      // Prioridad: detail (Problem+JSON) > message legacy > title > statusText.
      if (typeof data.detail === 'string' && data.detail) {
        message = data.detail;
      } else if (data.message) {
        message = Array.isArray(data.message) ? data.message.join(', ') : data.message;
      } else if (typeof data.title === 'string' && data.title) {
        message = data.title;
      }
    }
    if (!message) message = res.statusText || 'Request failed';
    return new ApiError(res.status, code, message, data, data?.errors);
  }
  return new ApiError(0, 'NETWORK_ERROR', error.message || 'Request failed');
}

// Adapter fetch: transporte nativo y compatible con la intercepción de MSW (tests).
const instance: AxiosInstance = axios.create({
  baseURL: apiBaseUrl,
  adapter: 'fetch',
  withCredentials: true,
  headers: { Accept: 'application/json' },
});

// Request interceptor: inyecta Bearer salvo en peticiones con auth:false.
instance.interceptors.request.use((config) => {
  const cfg = config as InternalConfig;
  if (cfg._auth !== false) {
    const token = getAuthRuntime()?.getAccessToken();
    if (token) config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

/**
 * Response interceptor. A 401 is NOT a synonym for "your session expired".
 *
 * The refresh is the only evidence that settles it, so the session is cleared
 * on exactly one outcome: a refresh that FAILED. Everything else leaves it
 * alone, because everything else is the server objecting to the request rather
 * than to the caller.
 *
 * That distinction used to be missing, and re-enrolling MFA is where it hurt:
 * `enroll/confirm` answers 401 on wrong TOTP digits, the refresh succeeded (the
 * session was fine all along), the retry replayed the already-spent code, the
 * second 401 was read as "session gone" — and the admin, who had simply
 * mistyped, landed on the login screen having burned 2 of their 10 attempts.
 */
instance.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const cfg = error.config as InternalConfig | undefined;
    const rt = getAuthRuntime();
    const authed = cfg?._auth !== false;
    if (error.response?.status === 401 && authed && rt) {
      if (cfg?._oneTimeSecret) {
        // The secret is spent. Replaying it can only fail again, and this 401
        // says nothing at all about the session — so neither is done.
      } else if (cfg && !cfg._retry) {
        const renewed = await rt.refresh();
        if (renewed) {
          cfg._retry = true;
          return instance.request(cfg);
        }
        // The refresh failed: the session really is gone.
        rt.onAuthFailure();
      } else if (!cfg) {
        // No config to judge by and nothing to retry. Clearing is the
        // conservative reading — the alternative is a session we cannot renew.
        rt.onAuthFailure();
      }
      // Already retried after a refresh that WORKED: the session is alive and
      // this 401 belongs to the request. Throwing it to login would sign the
      // operator out while holding tokens that still work.
    }
    throw toApiError(error);
  },
);

export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = true, idempotencyKey, signal, schema, oneTimeSecret } = opts;
  const config: InternalConfig = {
    url: path,
    method,
    data: body,
    signal,
    _auth: auth,
    _oneTimeSecret: oneTimeSecret,
  };
  const baseHeaders: Record<string, string> = {};
  if (idempotencyKey) baseHeaders['Idempotency-Key'] = idempotencyKey;

  let res: AxiosResponse<unknown>;
  let stepUpCode: string | null = null;
  for (;;) {
    config.headers = stepUpCode
      ? { ...baseHeaders, [STEP_UP_HEADER]: stepUpCode }
      : { ...baseHeaders };
    try {
      res = await instance.request<unknown>({ ...config });
      break;
    } catch (e) {
      const err =
        e instanceof ApiError
          ? e
          : axios.isAxiosError(e)
            ? toApiError(e)
            : new ApiError(0, 'UNKNOWN', e instanceof Error ? e.message : 'Request failed');
      // ko-backend#1059 — the action wants a fresh TOTP. Ask, and send the SAME
      // request again (same body, same Idempotency-Key) with the code; a
      // refused code asks again, a cancel throws the 403 as it came.
      const prompt = getStepUpPrompter();
      if (
        prompt &&
        err.status === 403 &&
        (err.code === STEP_UP_REQUIRED || err.code === STEP_UP_INVALID)
      ) {
        stepUpCode = await prompt({ invalid: err.code === STEP_UP_INVALID });
        if (stepUpCode) continue;
      }
      throw err;
    }
  }

  if (res.status === 204) return undefined as T;
  const json = res.data;
  if (schema) {
    const parsed = schema.safeParse(json);
    if (!parsed.success) {
      const issues = parsed.error.issues.map(
        (i) => `${i.path.join('.') || '(root)'}: ${i.message}`,
      );
      // A write already happened. Its body is a RECEIPT, not a permission, so a
      // receipt we cannot read is no reason to tell the operator the write did
      // not occur — they retry, the retry conflicts, and an action that
      // succeeded reads as one that never ran. That is exactly how a started
      // tournament ended up showing an error and then ALREADY_GENERATED.
      //
      // A read is the opposite case: nothing happened, and there is no
      // trustworthy data to paint. Failing renders the error state every screen
      // already handles, which is honest — we could not read it.
      const degraded = method !== 'GET';
      reportContractDrift({ path, method, status: res.status, issues, degraded });
      if (!degraded) {
        throw new ApiError(
          res.status,
          'CONTRACT_MISMATCH',
          'Respuesta no coincide con el contrato',
          parsed.error.format(),
        );
      }
      return json as T;
    }
    return parsed.data as T;
  }
  return json as T;
}
